import { useMemo, useState, useCallback, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { trpc } from "../lib/trpc";
import { Icon } from "../components/ui/Icon";
import { ToastStack, type ToastItem } from "../components/ui/Toast";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";
import { HardDeleteConfirm } from "../components/ui/HardDeleteConfirm";
import { FormBuilderDrawer } from "../components/forms/FormBuilderDrawer";
import { FormFillDrawer } from "../components/forms/FormFillDrawer";
import { FormTableSettingsDrawer, type FormTableSettings } from "../components/forms/FormTableSettingsDrawer";
import { FormCopyDialog, type CopySource } from "../components/forms/FormCopyDialog";
import { useIsMobile } from "../lib/useIsMobile";
import { MCard } from "../components/mobile/primitives";
import "./Items.css";
import "./Maintenance.css";
import "./Forms.css";

type Tab = "forms" | "records";
type FStatus = "draft" | "published" | "archived";

const STATUS_META: Record<FStatus, { cls: string; label: string; icon: string }> = {
  draft: { cls: "b-warn", label: "Taslak", icon: "edit" },
  published: { cls: "b-success", label: "Yayında", icon: "check" },
  archived: { cls: "", label: "Arşiv", icon: "archive" },
};

function fmtDate(d: string | Date | null): string { return d ? new Intl.DateTimeFormat("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(d)) : "—"; }

/** Kayıt tablosunda bir form alan-değerini tipe göre biçimlendir. */
function fmtFieldValue(v: unknown, type: string): string {
  if (v === null || v === undefined || v === "") return "—";
  if (type === "boolean") return v ? "Evet" : "Hayır";
  if (type === "number") { const n = Number(v); return Number.isFinite(n) ? new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 4 }).format(n) : String(v); }
  if (type === "date") return fmtDate(String(v));
  return String(v);
}
function fmtNum(n: number): string { return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 2 }).format(n); }
/** İçinde bulunulan ayın 1'i (YYYY-MM-01) — Kayıtlar varsayılan başlangıç filtresi. */
function currentMonthStart(): string { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`; }
const NO_VALUE_TYPES = new Set(["heading", "note", "table", "divider", "card"]); // yapısal/dizi → düz kayıt-kolonu/agregat değil
const FORM_TABLE_SETTINGS_KEY = "steperp.formTableSettings";

export function FormsPage() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const meQ = trpc.auth.me.useQuery(undefined, { staleTime: 5 * 60_000 });
  const isSuperAdmin = !!meQ.data?.user?.isSuperAdmin;
  const activeFacilityId = (meQ.data?.user as { activeFacilityId?: string | null } | undefined)?.activeFacilityId ?? null;
  const facilities = meQ.data?.user?.facilities ?? [];
  const facName = useCallback((id: string | null) => (id == null ? "Global" : facilities.find((f) => f.id === id)?.name ?? "—"), [facilities]);

  const [tab, setTab] = useState<Tab>("forms");
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const pushToast = useCallback((t: Omit<ToastItem, "id">) => {
    const id = `${t.tone}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setToasts((prev) => [...prev.slice(-4), { ...t, id }]);
  }, []);
  const dismissToast = useCallback((id: string) => setToasts((prev) => prev.filter((t) => t.id !== id)), []);

  // Drawers
  const [builderOpen, setBuilderOpen] = useState(false);
  const [builderTemplateId, setBuilderTemplateId] = useState<string | null>(null);
  const [fillOpen, setFillOpen] = useState(false);
  const [fillTemplateId, setFillTemplateId] = useState<string | null>(null);
  const [fillSubmissionId, setFillSubmissionId] = useState<string | null>(null);

  // ── Templates (Formlar sekmesi) ──
  const [statusFilter, setStatusFilter] = useState<FStatus | "">("");
  const tplQ = trpc.form.template.list.useQuery(isSuperAdmin && statusFilter ? { status: statusFilter } : undefined, { staleTime: 5_000 });
  const templates = tplQ.data ?? [];
  // Kayıtlar sekmesi (form filtresi + master otomatik-seçim) için Formlar-sekmesi statusFilter'ından BAĞIMSIZ,
  // daima yayınlı formlar — aksi halde "Arşiv/Taslak" filtresi seçiliyken master özelliği bozulurdu.
  const pubTplQ = trpc.form.template.list.useQuery({ status: "published" }, { staleTime: 5_000 });
  const utils = trpc.useUtils();

  const publishMut = trpc.form.template.publish.useMutation();
  const archiveMut = trpc.form.template.archive.useMutation();
  const removeMut = trpc.form.template.remove.useMutation();
  const setDefaultMut = trpc.form.template.setDefault.useMutation();
  const copyMut = trpc.form.template.copy.useMutation();
  const [copySource, setCopySource] = useState<CopySource | null>(null);

  const [archiveConfirm, setArchiveConfirm] = useState<{ id: string; name: string; archived: boolean } | null>(null);
  const [deleteTpl, setDeleteTpl] = useState<{ id: string; name: string } | null>(null);
  const [delErr, setDelErr] = useState<string | undefined>();

  async function doPublish(id: string) {
    try { await publishMut.mutateAsync({ id }); await utils.form.template.list.invalidate(); pushToast({ tone: "success", message: "Form yayınlandı — saha yöneticileri artık doldurabilir." }); }
    catch (e) { pushToast({ tone: "danger", title: "Yayınlanamadı", message: (e as Error).message }); }
  }
  async function doArchive() {
    if (!archiveConfirm) return;
    try { await archiveMut.mutateAsync({ id: archiveConfirm.id, archived: archiveConfirm.archived }); await utils.form.template.list.invalidate(); pushToast({ tone: "success", message: archiveConfirm.archived ? "Form arşivlendi." : "Form yayına alındı." }); }
    catch (e) { pushToast({ tone: "danger", message: (e as Error).message }); }
    finally { setArchiveConfirm(null); }
  }
  async function doSetDefault(id: string, isDefault: boolean) {
    try { await setDefaultMut.mutateAsync({ id, isDefault }); await utils.form.template.list.invalidate(); pushToast({ tone: "success", message: isDefault ? "Form varsayılan (master) yapıldı — Kayıtlar sekmesinde otomatik seçili gelir." : "Varsayılan işareti kaldırıldı." }); }
    catch (e) { pushToast({ tone: "danger", message: (e as Error).message }); }
  }
  async function doCopy(args: { targetTesisId: string | null; name: string }) {
    if (!copySource || copyMut.isPending) return; // çift-gönderim koruması
    try {
      const res = await copyMut.mutateAsync({ id: copySource.id, targetTesisId: args.targetTesisId, name: args.name });
      await utils.form.template.list.invalidate();
      const targetName = facName(args.targetTesisId); // null → "Global"
      // Süper-admin başka tesise scope'luyken (activeFacilityId) kopya listede görünmeyebilir → hedefe yönlendir.
      const visibleHere = activeFacilityId == null || args.targetTesisId == null || args.targetTesisId === activeFacilityId;
      pushToast({ tone: "success", message: `"${res.name}" formu ${targetName} için taslak olarak kopyalandı.${visibleHere ? " Düzenle ile içeriğini değiştirebilirsiniz." : ` Düzenlemek için üstten ${targetName} tesisine geçin.`}` });
      setCopySource(null);
    } catch (e) { pushToast({ tone: "danger", title: "Kopyalanamadı", message: (e as Error).message }); }
  }
  async function doDelete() {
    if (!deleteTpl) return;
    setDelErr(undefined);
    try { await removeMut.mutateAsync({ id: deleteTpl.id }); await utils.form.template.list.invalidate(); pushToast({ tone: "success", message: "Form silindi." }); setDeleteTpl(null); }
    catch (e) { setDelErr((e as Error).message); }
  }

  // ── Records (Kayıtlar sekmesi) ──
  const [recForm, setRecForm] = useState("");
  const [recFrom, setRecFrom] = useState(currentMonthStart); // varsayılan: ay başından itibaren
  const [recTo, setRecTo] = useState("");
  const [recPage, setRecPage] = useState(1);
  const recInput = useMemo(() => ({ formTemplateId: recForm || undefined, from: recFrom || undefined, to: recTo || undefined, page: recPage, pageSize: 25 }), [recForm, recFrom, recTo, recPage]);
  const recQ = trpc.form.submission.list.useQuery(recInput, { staleTime: 5_000, enabled: tab === "records" });
  const records = recQ.data?.rows ?? [];
  const recTotal = recQ.data?.total ?? 0;
  const recTotalPages = Math.max(1, Math.ceil(recTotal / 25));
  const removeSubMut = trpc.form.submission.remove.useMutation();
  const [deleteSub, setDeleteSub] = useState<{ id: string; formName: string } | null>(null);

  // ── Tablo ayarları: belirli form seçilince alan-değerleri kolon olur; kullanıcı "Tablo Ayarları" drawer'ından
  //    kolonları, kolon-bazlı alt-toplamı (Toplam/Ortalama) ve KPI kartlarını özelleştirir (forma özel, localStorage). ──
  const fieldsQ = trpc.form.template.get.useQuery({ id: recForm }, { enabled: !!recForm && tab === "records", staleTime: 30_000 });
  const valueFields = useMemo(() => (fieldsQ.data?.fields ?? []).filter((f) => !NO_VALUE_TYPES.has(f.type)), [fieldsQ.data]);
  const [settingsByForm, setSettingsByForm] = useState<Record<string, FormTableSettings>>(() => { try { return JSON.parse(localStorage.getItem(FORM_TABLE_SETTINGS_KEY) || "{}"); } catch { return {}; } });
  const curSettings: FormTableSettings = (recForm && settingsByForm[recForm]) || {};
  const activeCols = curSettings.cols ?? valueFields.map((f) => f.id); // varsayılan: tüm alanlar
  const shownFields = valueFields.filter((f) => activeCols.includes(f.id));
  const colAgg = curSettings.colAgg ?? {};
  const cards = curSettings.cards ?? [];
  const updateSettings = useCallback((next: FormTableSettings) => {
    if (!recForm) return;
    setSettingsByForm((prev) => {
      const merged = { ...prev, [recForm]: next };
      try { localStorage.setItem(FORM_TABLE_SETTINGS_KEY, JSON.stringify(merged)); } catch { /* yoksay */ }
      return merged;
    });
  }, [recForm]);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Alt-toplam / KPI kartı için forma-özel agregat (sayfalama-üstü, TÜM filtreli set; last = son değer).
  const numericShown = shownFields.filter((f) => f.type === "number");
  const anyColAgg = numericShown.some((f) => colAgg[f.id]?.sum || colAgg[f.id]?.avg);
  const showSumRow = numericShown.some((f) => colAgg[f.id]?.sum);
  const showAvgRow = numericShown.some((f) => colAgg[f.id]?.avg);
  const aggEnabled = !!recForm && (anyColAgg || cards.length > 0);
  const aggQ = trpc.form.submission.aggregate.useQuery(
    { formTemplateId: recForm, from: recFrom || undefined, to: recTo || undefined },
    { enabled: aggEnabled && tab === "records", staleTime: 5_000 },
  );
  const aggData = aggQ.data?.byField ?? {};

  async function doDeleteSub() {
    if (!deleteSub) return;
    try { await removeSubMut.mutateAsync({ id: deleteSub.id }); await utils.form.submission.list.invalidate(); pushToast({ tone: "success", message: "Kayıt silindi." }); }
    catch (e) { pushToast({ tone: "danger", message: (e as Error).message }); }
    finally { setDeleteSub(null); }
  }

  // Doldurulabilir (yayında) formlar — Kayıtlar filtresi + boş-durum linkleri (statusFilter'dan bağımsız pubTplQ).
  const publishedForms = pubTplQ.data ?? [];
  // "Master/varsayılan" form: aktif tesisin varsayılanı > global varsayılan > (son çare) herhangi bir varsayılan.
  // Aktif tesis yokken (süper-admin "Tüm Tesisler") keyfi bir tesis varsayılanı yerine global tercih edilir.
  const defaultForm = useMemo(() => {
    const facilityDefault = activeFacilityId ? publishedForms.find((t) => t.isDefault && t.tesisId === activeFacilityId) : undefined;
    return facilityDefault ?? publishedForms.find((t) => t.isDefault && !t.tesisId) ?? publishedForms.find((t) => t.isDefault);
  }, [publishedForms, activeFacilityId]);
  // "Tümü" seçeneği YOK → Kayıtlar sekmesinde recForm DAİMA geçerli bir yayınlı form olmalı.
  // İlk giriş (boş) veya seçili form yayından kalkınca (arşiv/sil) → varsayılan (master) form, yoksa
  // ilk yayınlı form seçilir. Seçim geçerliyken zorlanmaz → kullanıcı formlar arasında serbestçe geçer.
  useEffect(() => {
    if (tab !== "records" || !pubTplQ.data) return;
    const valid = recForm && publishedForms.some((t) => t.id === recForm);
    if (!valid && publishedForms.length > 0) { setRecForm((defaultForm ?? publishedForms[0]!).id); setRecPage(1); }
  }, [tab, pubTplQ.data, publishedForms, recForm, defaultForm]);

  const drawers = (
    <>
      {isSuperAdmin ? (
        <FormBuilderDrawer open={builderOpen} templateId={builderTemplateId} onClose={() => setBuilderOpen(false)} onSaved={(msg) => pushToast({ tone: "success", message: msg })} />
      ) : null}
      <FormFillDrawer open={fillOpen} templateId={fillTemplateId} submissionId={fillSubmissionId} onClose={() => setFillOpen(false)} onSaved={(msg) => pushToast({ tone: "success", message: msg })} />
      <FormTableSettingsDrawer open={settingsOpen} onClose={() => setSettingsOpen(false)} fields={valueFields} settings={curSettings} onChange={updateSettings} />
      <FormCopyDialog open={!!copySource} source={copySource} facilities={facilities} busy={copyMut.isPending} onConfirm={doCopy} onCancel={() => setCopySource(null)} />

      <ConfirmDialog
        open={!!archiveConfirm}
        title={archiveConfirm?.archived ? "Formu arşivle" : "Formu yayına al"}
        message={archiveConfirm?.archived ? `"${archiveConfirm?.name}" arşivlenecek — yeni doldurma yapılamaz, geçmiş kayıtlar korunur.` : `"${archiveConfirm?.name}" yeniden yayına alınacak.`}
        confirmLabel={archiveConfirm?.archived ? "Arşivle" : "Yayına al"}
        tone="primary"
        busy={archiveMut.isPending}
        onConfirm={doArchive}
        onCancel={() => setArchiveConfirm(null)}
      />
      <HardDeleteConfirm
        open={!!deleteTpl}
        code="SIL"
        title={`Formu kalıcı sil: ${deleteTpl?.name ?? ""}`}
        warning="Bu form ve tüm alanları kalıcı olarak silinecek. Bu işlem geri alınamaz. (Kaydı olan formlar silinemez — bunun yerine arşivleyin.)"
        busy={removeMut.isPending}
        errorMessage={delErr}
        onConfirmed={doDelete}
        onClose={() => setDeleteTpl(null)}
      />
      <ConfirmDialog
        open={!!deleteSub}
        title="Kaydı sil"
        message={`"${deleteSub?.formName}" formunun bu kaydı kalıcı olarak silinecek.`}
        confirmLabel="Sil"
        tone="danger"
        busy={removeSubMut.isPending}
        onConfirm={doDeleteSub}
        onCancel={() => setDeleteSub(null)}
      />

      <ToastStack toasts={toasts} onDismiss={dismissToast} />
    </>
  );

  if (isMobile) {
    const mb = (cls: string) => (cls === "b-warn" ? "b-warning" : cls || "b-neutral");
    return (
      <>
        <div className="m-stickytop">
          {isSuperAdmin ? (
            <div style={{ display: "flex", gap: 8 }}>
              <button type="button" className="btn-md secondary" style={{ flex: 1, justifyContent: "center" }} onClick={() => navigate("/formlar/panel")}><Icon name="chart" size={14} />Panel</button>
              <button type="button" className="btn-md primary" style={{ flex: 1, justifyContent: "center" }} onClick={() => { setBuilderTemplateId(null); setBuilderOpen(true); }}><Icon name="plus" size={14} />Yeni Form</button>
            </div>
          ) : null}
          <div className="m-chips">
            <button type="button" className={`m-chip${tab === "forms" ? " act" : ""}`} onClick={() => setTab("forms")}>Formlar{tplQ.data ? ` (${templates.length})` : ""}</button>
            <button type="button" className={`m-chip${tab === "records" ? " act" : ""}`} onClick={() => setTab("records")}>Kayıtlar{recQ.data ? ` (${recTotal})` : ""}</button>
          </div>
        </div>

        {/* ── Formlar ── */}
        {tab === "forms" ? (
          <>
            {isSuperAdmin ? (
              <div className="m-chips">
                {([["", "Tümü"], ...Object.entries(STATUS_META).map(([v, m]) => [v, m.label] as [string, string])] as Array<[string, string]>).map(([v, lb]) => (
                  <button key={lb} type="button" className={`m-chip${statusFilter === v ? " act" : ""}`} onClick={() => setStatusFilter(v as FStatus | "")}>{lb}</button>
                ))}
              </div>
            ) : null}
            {tplQ.isLoading ? <div className="m-empty">Yükleniyor…</div> : templates.length === 0 ? (
              <div className="m-empty">{isSuperAdmin ? "Henüz form yok" : "Doldurulabilir form yok"}</div>
            ) : (
              templates.map((t) => {
                const m = STATUS_META[t.status as FStatus];
                return (
                  <MCard pad key={t.id}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13.5, fontWeight: 700, color: "var(--neutral-900)" }}>
                          {t.name}
                          {t.isDefault ? <span className="badge b-primary no-dot" style={{ flexShrink: 0 }}><Icon name="star" size={10} style={{ fill: "currentColor" }} />Varsayılan</span> : null}
                        </span>
                        {t.description ? <span style={{ display: "block", fontSize: 11.5, color: "var(--neutral-500)" }}>{t.description}</span> : null}
                      </span>
                      <span className={`badge ${mb(m.cls)} no-dot`}><Icon name={m.icon} size={11} />{m.label}</span>
                    </div>
                    <div style={{ display: "flex", gap: 16, marginTop: 8, fontSize: 11.5, color: "var(--neutral-600)" }}>
                      {isSuperAdmin ? <span><span style={{ color: "var(--neutral-400)" }}>Tesis </span>{facName(t.tesisId)}</span> : null}
                      <span><span style={{ color: "var(--neutral-400)" }}>Alan </span><b className="mono">{t.fieldCount}</b></span>
                      <span><span style={{ color: "var(--neutral-400)" }}>Kayıt </span><b className="mono">{t.submissionCount > 0 ? t.submissionCount : "—"}</b></span>
                    </div>
                    <div style={{ display: "flex", gap: 6, marginTop: 10, justifyContent: "flex-end", flexWrap: "wrap", alignItems: "center" }}>
                      {t.status === "published" ? (
                        <button type="button" className="btn-md secondary sm" onClick={() => { setFillSubmissionId(null); setFillTemplateId(t.id); setFillOpen(true); }}><Icon name="edit" size={12} />Doldur</button>
                      ) : null}
                      {isSuperAdmin ? (
                        <>
                          {t.status === "published" ? (
                            <button type="button" className={`m-iconbtn m-li-act${t.isDefault ? " on" : ""}`} aria-label={t.isDefault ? "Varsayılanı kaldır" : "Varsayılan (master) yap"} onClick={() => doSetDefault(t.id, !t.isDefault)}><Icon name="star" size={16} style={t.isDefault ? { fill: "currentColor" } : undefined} /></button>
                          ) : null}
                          <button type="button" className="m-iconbtn m-li-act" aria-label="Başka tesise kopyala" onClick={() => setCopySource({ id: t.id, name: t.name, tesisId: t.tesisId })}><Icon name="copy" size={16} /></button>
                          <button type="button" className="m-iconbtn m-li-act" aria-label="Düzenle" onClick={() => { setBuilderTemplateId(t.id); setBuilderOpen(true); }}><Icon name="edit" size={16} /></button>
                          {t.status === "draft" ? <button type="button" className="m-iconbtn m-li-act" style={{ color: "var(--success)" }} aria-label="Yayınla" onClick={() => doPublish(t.id)}><Icon name="check" size={16} /></button> : null}
                          {t.status === "published" ? <button type="button" className="m-iconbtn m-li-act" aria-label="Arşivle" onClick={() => setArchiveConfirm({ id: t.id, name: t.name, archived: true })}><Icon name="archive" size={16} /></button> : null}
                          {t.status === "archived" ? <button type="button" className="m-iconbtn m-li-act" aria-label="Yayına al" onClick={() => setArchiveConfirm({ id: t.id, name: t.name, archived: false })}><Icon name="chevronUp" size={16} /></button> : null}
                          <button type="button" className="m-iconbtn m-li-act danger" aria-label="Sil" disabled={t.submissionCount > 0} onClick={() => { setDelErr(undefined); setDeleteTpl({ id: t.id, name: t.name }); }}><Icon name="trash" size={16} /></button>
                        </>
                      ) : null}
                    </div>
                  </MCard>
                );
              })
            )}
          </>
        ) : null}

        {/* ── Kayıtlar ── */}
        {tab === "records" ? (
          <>
            <label style={{ display: "block" }}>
              <span className="m-ovl" style={{ display: "block", marginBottom: 3 }}>Form</span>
              <select className="form-select" value={recForm} onChange={(e) => { setRecForm(e.target.value); setRecPage(1); }}>
                {publishedForms.map((t) => <option key={t.id} value={t.id}>{t.name}{t.isDefault ? " (varsayılan)" : ""}</option>)}
              </select>
            </label>
            <div style={{ display: "flex", gap: 8 }}>
              <label style={{ flex: 1 }}>
                <span className="m-ovl" style={{ display: "block", marginBottom: 3 }}>Başlangıç</span>
                <input className="form-input" type="date" value={recFrom} onChange={(e) => { setRecFrom(e.target.value); setRecPage(1); }} />
              </label>
              <label style={{ flex: 1 }}>
                <span className="m-ovl" style={{ display: "block", marginBottom: 3 }}>Bitiş</span>
                <input className="form-input" type="date" value={recTo} onChange={(e) => { setRecTo(e.target.value); setRecPage(1); }} />
              </label>
            </div>
            {recForm && valueFields.length > 0 ? (
              <button type="button" className="btn-md secondary sm" style={{ alignSelf: "flex-start" }} onClick={() => setSettingsOpen(true)}><Icon name="settings" size={14} />Tablo Ayarları ({shownFields.length}/{valueFields.length}{cards.length ? ` · ${cards.length} KPI` : ""})</button>
            ) : null}

            {recForm && cards.length > 0 ? (
              <div className="dash-kpi-grid">
                {cards.map((c) => {
                  const field = valueFields.find((f) => f.id === c.fieldId);
                  const a = aggData[c.fieldId];
                  const val = a ? (c.agg === "sum" ? a.sum : c.agg === "avg" ? a.avg : a.last) : null;
                  const aggLbl = c.agg === "sum" ? "Toplam" : c.agg === "avg" ? "Ortalama" : "Son Değer";
                  const kpiColor = c.agg === "sum" ? "var(--primary)" : c.agg === "avg" ? "var(--info)" : "var(--success)";
                  return (
                    <div className="dash-kpi" key={c.id} style={{ ["--kpi-color" as string]: kpiColor }}>
                      <div className="head"><span className="lbl">{c.title || field?.label || "—"}</span></div>
                      <div className="val">{val != null ? fmtNum(val) : "—"}{field?.unit ? <span className="unit">{field.unit}</span> : null}</div>
                      <div className="sub">{aggLbl}{aggQ.data ? ` · ${aggQ.data.total} kayıt` : ""}</div>
                    </div>
                  );
                })}
              </div>
            ) : null}

            {recQ.isLoading ? <div className="m-empty">Yükleniyor…</div> : records.length === 0 ? (
              <div className="m-empty">{(recFrom || recTo) ? "Filtreye uygun kayıt yok." : "Bu formda henüz kayıt yok."}</div>
            ) : (
              <>
                {records.map((s) => (
                  <MCard pad key={s.id} onClick={() => { setFillTemplateId(null); setFillSubmissionId(s.id); setFillOpen(true); }} style={{ cursor: "pointer" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span className="mono" style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 700, color: "var(--neutral-900)" }}>{fmtDate(s.filledForDate)}</span>
                      <button type="button" className="m-iconbtn m-li-act danger" aria-label="Sil" onClick={(e) => { e.stopPropagation(); setDeleteSub({ id: s.id, formName: s.formName }); }}><Icon name="trash" size={16} /></button>
                    </div>
                    {shownFields.length > 0 ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 8 }}>
                        {shownFields.map((f) => {
                          const isNum = f.type === "number" || f.type === "date" || f.type === "time";
                          return (
                            <div key={f.id} style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 12 }}>
                              <span style={{ color: "var(--neutral-500)", flexShrink: 0 }}>{f.label}{f.unit ? ` (${f.unit})` : ""}</span>
                              <span style={{ fontWeight: 600, fontFamily: isNum ? "var(--font-mono)" : undefined, textAlign: "right", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{fmtFieldValue(s.data[f.id], f.type)}</span>
                            </div>
                          );
                        })}
                      </div>
                    ) : null}
                  </MCard>
                ))}
                {recTotalPages > 1 ? (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, marginTop: 2 }}>
                    <button type="button" className="btn-md secondary sm" disabled={recPage <= 1} onClick={() => setRecPage((p) => Math.max(1, p - 1))}>‹ Önceki</button>
                    <span className="mono" style={{ fontSize: 12.5, color: "var(--neutral-500)" }}>{recPage} / {recTotalPages}</span>
                    <button type="button" className="btn-md secondary sm" disabled={recPage >= recTotalPages} onClick={() => setRecPage((p) => Math.min(recTotalPages, p + 1))}>Sonraki ›</button>
                  </div>
                ) : null}
              </>
            )}
          </>
        ) : null}

        {drawers}
      </>
    );
  }

  return (
    <div className="items-page">
      <div className="items-crumb">
        <Link to="/dashboard" style={{ color: "var(--neutral-500)", textDecoration: "none" }}>Panel</Link>
        <span className="sep">/</span>
        <span className="cur">Formlar</span>
      </div>

      <div className="items-head">
        <div>
          <h1 className="title">Formlar</h1>
          <p className="sub">{isSuperAdmin ? "Saha yöneticileri için özelleştirilmiş form tasarla · vardiya bazlı doldurma + kayıt yönetimi" : "Vardiya bazlı saha formlarını doldur · geçmiş kayıtları düzenle"}</p>
        </div>
        {isSuperAdmin ? (
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" className="btn-md secondary" onClick={() => navigate("/formlar/panel")}><Icon name="chart" size={14} />Panel</button>
            <button type="button" className="btn-md primary" onClick={() => { setBuilderTemplateId(null); setBuilderOpen(true); }}><Icon name="plus" size={14} />Yeni Form</button>
          </div>
        ) : null}
      </div>

      <div className="dt2-wrap">
        <div className="dt2-tabs">
          <button type="button" className={`dt2-tab ${tab === "forms" ? "active" : ""}`} onClick={() => setTab("forms")}>Formlar{tplQ.data ? <span className="ct">{templates.length}</span> : null}</button>
          <button type="button" className={`dt2-tab ${tab === "records" ? "active" : ""}`} onClick={() => setTab("records")}>Kayıtlar{recQ.data ? <span className="ct">{recTotal}</span> : null}</button>
        </div>

        {/* ── SEKME: Formlar ── */}
        {tab === "forms" ? (
          <>
            {isSuperAdmin ? (
              <div className="mt-filters">
                <div className="mt-filter">
                  <span className="mt-filter-label">Durum</span>
                  <select className="form-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as FStatus | "")}>
                    <option value="">Tümü</option>
                    {Object.entries(STATUS_META).map(([v, m]) => <option key={v} value={v}>{m.label}</option>)}
                  </select>
                </div>
                {statusFilter ? <button type="button" className="btn-md ghost" style={{ alignSelf: "flex-end" }} onClick={() => setStatusFilter("")}>Temizle</button> : null}
              </div>
            ) : null}
            <div className="dt2-scroll">
              {tplQ.isLoading ? <div className="mt-empty">Yükleniyor…</div> : templates.length === 0 ? (
                <div className="empty-state">
                  <p className="ttl">{isSuperAdmin ? "Henüz form yok" : "Doldurulabilir form yok"}</p>
                  <p className="sub">{isSuperAdmin ? "\"Yeni Form\" ile saha yöneticileri için özelleştirilmiş bir form tasarlayın." : "Sistem yöneticisi form yayınladığında burada görünecek."}</p>
                </div>
              ) : (
                <table className="dt2">
                  <thead>
                    <tr>
                      <th>Form</th>
                      {isSuperAdmin ? <th style={{ width: 130 }}>Tesis</th> : null}
                      <th className="num" style={{ width: 70 }}>Alan</th>
                      <th className="num" style={{ width: 80 }}>Kayıt</th>
                      <th style={{ width: 110 }}>Durum</th>
                      <th style={{ width: 220 }} aria-label="İşlemler"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {templates.map((t) => {
                      const m = STATUS_META[t.status as FStatus];
                      return (
                        <tr key={t.id}>
                          <td>
                            <div style={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 7 }}>
                              {t.name}
                              {t.isDefault ? <span className="badge-pill b-primary" title="Master form — Kayıtlar sekmesinde otomatik seçili gelir"><Icon name="star" size={10} style={{ fill: "currentColor" }} />Varsayılan</span> : null}
                            </div>
                            {t.description ? <div style={{ fontSize: 11.5, color: "var(--neutral-500)" }}>{t.description}</div> : null}
                          </td>
                          {isSuperAdmin ? <td style={{ fontSize: 12 }}>{facName(t.tesisId)}</td> : null}
                          <td className="cell-num">{t.fieldCount}</td>
                          <td className="cell-num">{t.submissionCount > 0 ? t.submissionCount : "—"}</td>
                          <td><span className={`badge-pill ${m.cls}`}><Icon name={m.icon} size={11} />{m.label}</span></td>
                          <td>
                            <div className="row-acts" style={{ justifyContent: "flex-end" }}>
                              {t.status === "published" ? (
                                <button type="button" className="btn-md secondary" style={{ height: 28, padding: "0 10px", fontSize: 12.5 }} onClick={() => { setFillSubmissionId(null); setFillTemplateId(t.id); setFillOpen(true); }}><Icon name="edit" size={12} />Doldur</button>
                              ) : null}
                              {isSuperAdmin ? (
                                <>
                                  {t.status === "published" ? (
                                    <button type="button" className={`iact${t.isDefault ? " on" : ""}`} aria-label={t.isDefault ? "Varsayılanı kaldır" : "Varsayılan (master) yap"} title={t.isDefault ? "Varsayılan (master) — kaldırmak için tıkla" : "Master yap — Kayıtlar sekmesinde otomatik seçili gelir"} onClick={() => doSetDefault(t.id, !t.isDefault)}><Icon name="star" size={14} style={t.isDefault ? { fill: "currentColor" } : undefined} /></button>
                                  ) : null}
                                  <button type="button" className="iact" aria-label="Başka tesise kopyala" title="Başka tesise kopyala (taslak)" onClick={() => setCopySource({ id: t.id, name: t.name, tesisId: t.tesisId })}><Icon name="copy" size={14} /></button>
                                  <button type="button" className="iact" aria-label="Düzenle" title="Düzenle" onClick={() => { setBuilderTemplateId(t.id); setBuilderOpen(true); }}><Icon name="edit" size={14} /></button>
                                  {t.status === "draft" ? <button type="button" className="iact" aria-label="Yayınla" title="Yayınla" onClick={() => doPublish(t.id)}><Icon name="check" size={14} /></button> : null}
                                  {t.status === "published" ? <button type="button" className="iact" aria-label="Arşivle" title="Arşivle" onClick={() => setArchiveConfirm({ id: t.id, name: t.name, archived: true })}><Icon name="archive" size={14} /></button> : null}
                                  {t.status === "archived" ? <button type="button" className="iact" aria-label="Yayına al" title="Yayına al" onClick={() => setArchiveConfirm({ id: t.id, name: t.name, archived: false })}><Icon name="chevronUp" size={14} /></button> : null}
                                  <button type="button" className="iact danger" aria-label="Sil" title={t.submissionCount > 0 ? "Kaydı olan form silinemez — arşivleyin" : "Sil"} disabled={t.submissionCount > 0} onClick={() => { setDelErr(undefined); setDeleteTpl({ id: t.id, name: t.name }); }}><Icon name="trash" size={14} /></button>
                                </>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </>
        ) : null}

        {/* ── SEKME: Kayıtlar ── */}
        {tab === "records" ? (
          <>
            <div className="mt-filters">
              <div className="mt-filter">
                <span className="mt-filter-label">Form</span>
                <select className="form-select" value={recForm} onChange={(e) => { setRecForm(e.target.value); setRecPage(1); }}>
                  {publishedForms.map((t) => <option key={t.id} value={t.id}>{t.name}{t.isDefault ? " (varsayılan)" : ""}</option>)}
                </select>
              </div>
              <div className="mt-filter">
                <span className="mt-filter-label">Başlangıç</span>
                <input className="form-input" type="date" value={recFrom} onChange={(e) => { setRecFrom(e.target.value); setRecPage(1); }} />
              </div>
              <div className="mt-filter">
                <span className="mt-filter-label">Bitiş</span>
                <input className="form-input" type="date" value={recTo} onChange={(e) => { setRecTo(e.target.value); setRecPage(1); }} />
              </div>
              {(recFrom || recTo) ? <button type="button" className="btn-md ghost" style={{ alignSelf: "flex-end" }} onClick={() => { setRecFrom(""); setRecTo(""); setRecPage(1); }}>Temizle</button> : null}
              {recForm && valueFields.length > 0 ? (
                <button type="button" className="btn-md secondary" style={{ alignSelf: "flex-end", marginLeft: "auto" }} onClick={() => setSettingsOpen(true)} title="Kolonlar, alt-toplamlar ve KPI kartlarını düzenle">
                  <Icon name="settings" size={14} />Tablo Ayarları<span style={{ color: "var(--neutral-500)", fontSize: 11.5, marginLeft: 2 }}>({shownFields.length}/{valueFields.length}{cards.length ? ` · ${cards.length} KPI` : ""})</span>
                </button>
              ) : null}
            </div>

            {/* ── KPI Kartları (Tablo Ayarları'ndan eklenir) — tablo üstünde özet ── */}
            {recForm && cards.length > 0 ? (
              <div className="dash-kpi-grid" style={{ padding: "12px 14px", borderBottom: "1px solid var(--neutral-200)", background: "var(--white)" }}>
                {cards.map((c) => {
                  const field = valueFields.find((f) => f.id === c.fieldId);
                  const a = aggData[c.fieldId];
                  const val = a ? (c.agg === "sum" ? a.sum : c.agg === "avg" ? a.avg : a.last) : null;
                  const aggLbl = c.agg === "sum" ? "Toplam" : c.agg === "avg" ? "Ortalama" : "Son Değer";
                  const kpiColor = c.agg === "sum" ? "var(--primary)" : c.agg === "avg" ? "var(--info)" : "var(--success)";
                  return (
                    <div className="dash-kpi" key={c.id} style={{ ["--kpi-color" as string]: kpiColor }}>
                      <div className="head"><span className="lbl">{c.title || field?.label || "—"}</span></div>
                      <div className="val">{val != null ? fmtNum(val) : "—"}{field?.unit ? <span className="unit">{field.unit}</span> : null}</div>
                      <div className="sub">{field?.label ?? "—"} · {aggLbl}{aggQ.data ? ` · ${aggQ.data.total} kayıt` : ""}</div>
                    </div>
                  );
                })}
              </div>
            ) : null}
            <div className="dt2-scroll">
              {recQ.isLoading ? <div className="mt-empty">Yükleniyor…</div> : records.length === 0 ? (
                <div className="empty-state"><p className="ttl">Kayıt yok</p><p className="sub">{(recFrom || recTo) ? "Filtreye uygun kayıt yok." : "Bu formda henüz kayıt yok — Formlar sekmesinden doldurabilirsiniz."}</p></div>
              ) : (
                <table className="dt2">
                  <thead><tr><th style={{ width: 120 }}>Tarih</th>{shownFields.map((f) => <th key={f.id} title={f.label}>{f.label}{f.unit ? <span style={{ color: "var(--neutral-400)", fontWeight: 400, marginLeft: 3 }}>({f.unit})</span> : null}</th>)}<th style={{ width: 90 }} aria-label="İşlemler"></th></tr></thead>
                  <tbody>
                    {records.map((s) => (
                      <tr key={s.id} onClick={() => { setFillTemplateId(null); setFillSubmissionId(s.id); setFillOpen(true); }} style={{ cursor: "pointer" }} title="Kaydı düzenle">
                        <td style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>{fmtDate(s.filledForDate)}</td>
                        {shownFields.map((f) => { const isNum = f.type === "number" || f.type === "date" || f.type === "time"; return <td key={f.id} style={{ fontFamily: isNum ? "var(--font-mono)" : undefined, fontSize: 12.5, whiteSpace: f.type === "textarea" ? "normal" : "nowrap", maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis" }} title={fmtFieldValue(s.data[f.id], f.type)}>{fmtFieldValue(s.data[f.id], f.type)}</td>; })}
                        <td>
                          <div className="row-acts" style={{ justifyContent: "flex-end" }}>
                            <button type="button" className="iact danger" aria-label="Sil" title="Sil" onClick={(e) => { e.stopPropagation(); setDeleteSub({ id: s.id, formName: s.formName }); }}><Icon name="trash" size={14} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {showSumRow || showAvgRow ? (
                    <tfoot>
                      {showSumRow ? (
                        <tr style={{ background: "var(--neutral-50)" }}>
                          <td style={{ fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--neutral-600)" }}>Toplam <span style={{ fontWeight: 400, textTransform: "none", color: "var(--neutral-400)" }}>({aggQ.data?.total ?? 0} kayıt)</span></td>
                          {shownFields.map((f) => { const a = aggData[f.id]; const on = f.type === "number" && colAgg[f.id]?.sum && a; return <td key={f.id} style={{ fontFamily: "var(--font-mono)", fontSize: 12.5, fontWeight: 700, color: "var(--neutral-800)", whiteSpace: "nowrap" }}>{on ? `${fmtNum(a.sum)}${f.unit ? " " + f.unit : ""}` : ""}</td>; })}
                          <td></td>
                        </tr>
                      ) : null}
                      {showAvgRow ? (
                        <tr style={{ background: "var(--neutral-50)" }}>
                          <td style={{ fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--neutral-600)" }}>Ortalama <span style={{ fontWeight: 400, textTransform: "none", color: "var(--neutral-400)" }}>({aggQ.data?.total ?? 0} kayıt)</span></td>
                          {shownFields.map((f) => { const a = aggData[f.id]; const on = f.type === "number" && colAgg[f.id]?.avg && a; return <td key={f.id} style={{ fontFamily: "var(--font-mono)", fontSize: 12.5, fontWeight: 600, color: "var(--info)", whiteSpace: "nowrap" }}>{on ? `${fmtNum(a.avg)}${f.unit ? " " + f.unit : ""}` : ""}</td>; })}
                          <td></td>
                        </tr>
                      ) : null}
                    </tfoot>
                  ) : null}
                </table>
              )}
            </div>
            <div className="dt2-foot">
              <span>{recTotal} kayıt</span>
              <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                <button type="button" className="pg" onClick={() => setRecPage((p) => Math.max(1, p - 1))} disabled={recPage <= 1} aria-label="Önceki">‹</button>
                <span className="mono">{recPage}</span>
                <span style={{ color: "var(--neutral-400)", fontSize: 12 }}>/ {recTotalPages}</span>
                <button type="button" className="pg" onClick={() => setRecPage((p) => Math.min(recTotalPages, p + 1))} disabled={recPage >= recTotalPages} aria-label="Sonraki">›</button>
              </span>
            </div>
          </>
        ) : null}
      </div>

      {drawers}
    </div>
  );
}
